import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Ambulance, ArrowLeft, CheckCircle2, FileDown } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { buildW9Pdf } from "@/lib/w9";

const CLASSIFICATIONS = [
  { value: "individual", label: "Individual / sole proprietor" },
  { value: "llc", label: "LLC" },
  { value: "other", label: "Other" },
];

const digitsOnly = (s, max) => (s || "").replace(/\D/g, "").slice(0, max);
const normalizeName = (s) => (s || "").trim().replace(/\s+/g, " ").toLowerCase();
const todayIso = () => new Date().toISOString().slice(0, 10);

// Mobile-first driver page: builds a filled, typed-signed W-9 in the browser with pdf-lib
// and uploads it as the driver's w9 document, or the driver can skip this and upload a
// signed scan from the documents panel instead (that plain upload path already works once
// the w9 doc type exists on the backend, no page needed).
export default function DriverW9() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [name, setName] = useState(user?.name || "");
  const [editingName, setEditingName] = useState(false);
  const [businessName, setBusinessName] = useState("");
  const [classification, setClassification] = useState("individual");
  const [llcClass, setLlcClass] = useState("");
  const [otherDescription, setOtherDescription] = useState("");

  const [addrStreet, setAddrStreet] = useState("");
  const [addrCity, setAddrCity] = useState("");
  const [addrState, setAddrState] = useState("");
  const [addrZip, setAddrZip] = useState("");

  const [idType, setIdType] = useState("ssn");
  const [ssn1, setSsn1] = useState("");
  const [ssn2, setSsn2] = useState("");
  const [ssn3, setSsn3] = useState("");
  const [ein1, setEin1] = useState("");
  const [ein2, setEin2] = useState("");

  const [signature, setSignature] = useState("");
  const [busy, setBusy] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [filed, setFiled] = useState(null); // { date }

  // Best-effort address prefill; this endpoint may not exist yet on the backend, so a
  // failure of any kind just leaves the address fields blank for manual entry.
  useEffect(() => {
    let cancelled = false;
    if (!user?.driver_id) return undefined;
    (async () => {
      try {
        const r = await api.get(`/drivers/${user.driver_id}/license-prefill`);
        if (cancelled || r.status !== 200) return;
        const d = r.data || {};
        if (d.addr_street) setAddrStreet(d.addr_street);
        if (d.addr_city) setAddrCity(d.addr_city);
        if (d.addr_state) setAddrState(d.addr_state);
        if (d.addr_zip) setAddrZip(d.addr_zip);
      } catch {
        // Endpoint not merged yet, or the driver has no license on file. Not an error here.
      }
    })();
    return () => { cancelled = true; };
  }, [user?.driver_id]);

  // Show the already-filed state on return visits instead of an empty form.
  useEffect(() => {
    let cancelled = false;
    if (!user?.driver_id) return undefined;
    api.get(`/drivers/${user.driver_id}/documents`).then((r) => {
      if (cancelled) return;
      const row = (r.data?.checklist || []).find((c) => c.doc_type === "w9");
      if (row?.latest) setFiled({ date: row.latest.uploaded_at });
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [user?.driver_id]);

  // The SSN/EIN digits live only in this component's state, are never sent anywhere but
  // inside the built PDF bytes, and are wiped the moment this page goes away.
  useEffect(() => () => {
    setSsn1(""); setSsn2(""); setSsn3("");
    setEin1(""); setEin2("");
  }, []);

  useEffect(() => () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewUrl]);

  const idComplete = idType === "ssn"
    ? ssn1.length === 3 && ssn2.length === 2 && ssn3.length === 4
    : ein1.length === 2 && ein2.length === 7;

  const signatureMatches = signature.trim().length > 0 && normalizeName(signature) === normalizeName(name);

  // The IRS form's own line 3a: an LLC must also pick its tax classification letter (C, S or
  // P), and Other must say what it is. Both are required inputs on this page, not left blank
  // for an admin to chase down after the fact.
  const classificationComplete = classification === "llc" ? !!llcClass
    : classification === "other" ? otherDescription.trim().length > 0
    : true;

  const canSign = useMemo(
    () => !!name.trim() && idComplete && signatureMatches && classificationComplete && !busy,
    [name, idComplete, signatureMatches, classificationComplete, busy]
  );

  const buildBytes = (signedOn) => buildW9Pdf({
    name: name.trim(),
    businessName: businessName.trim(),
    classification,
    llcClass: classification === "llc" ? llcClass : null,
    otherDescription: classification === "other" ? otherDescription.trim() : null,
    addrStreet,
    addrCity,
    addrState,
    addrZip,
    ssn: idType === "ssn" && idComplete ? [ssn1, ssn2, ssn3] : null,
    ein: idType === "ein" && idComplete ? [ein1, ein2] : null,
    signature: signature.trim(),
    signedOn,
  });

  const doPreview = async () => {
    if (!name.trim()) return toast.error("Enter your name first");
    setBusy(true);
    try {
      const bytes = await buildBytes();
      const blob = new Blob([bytes], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      setPreviewUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return url; });
    } catch (e) {
      toast.error(`Could not build the PDF: ${e?.message || e}`);
    } finally {
      setBusy(false);
    }
  };

  const doSign = async () => {
    if (!canSign) return;
    setBusy(true);
    const date = todayIso();
    try {
      const bytes = await buildBytes(date);
      const blob = new Blob([bytes], { type: "application/pdf" });
      const fd = new FormData();
      fd.append("doc_type", "w9");
      fd.append("file", blob, `W-9_${date}.pdf`);
      await api.post(`/drivers/${user.driver_id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSsn1(""); setSsn2(""); setSsn3("");
      setEin1(""); setEin2("");
      setSignature("");
      setFiled({ date });
      toast.success("W-9 filed");
    } catch (e) {
      toast.error(`Could not file the W-9: ${formatApiError(e.response?.data?.detail) || e?.message || e}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-[#0A192F] text-white">
        <div className="max-w-lg mx-auto px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-md bg-[#005E82] flex items-center justify-center"><Ambulance size={18} /></div>
            <div>
              <div className="font-display font-semibold text-sm">IRS Form W-9</div>
              <div className="text-[11px] text-slate-400 truncate max-w-[180px]">{user?.name}</div>
            </div>
          </div>
          <button onClick={() => navigate("/driver")} className="min-h-[44px] text-slate-300 text-xs flex items-center gap-1.5 hover:text-white" data-testid="driver-w9-back">
            <ArrowLeft size={14} /> Back to rides
          </button>
        </div>
      </header>

      <div className="max-w-lg mx-auto px-4 pt-5 pb-24" data-testid="driver-w9">
        {filed ? (
          <div className="bg-white border border-emerald-200 rounded-md p-6 text-center" data-testid="driver-w9-filed">
            <CheckCircle2 size={32} className="text-emerald-600 mx-auto mb-3" />
            <div className="font-display font-semibold text-slate-900 text-lg">Filed</div>
            <div className="text-sm text-slate-600 mt-1">Your W-9 was uploaded on {filed.date} and is pending review.</div>
            <button
              onClick={() => setFiled(null)}
              className="mt-4 min-h-[44px] text-sm text-[#005E82] hover:underline"
              data-testid="driver-w9-refile"
            >
              File a new one instead
            </button>
            <div className="mt-2">
              <button onClick={() => navigate("/driver")} className="min-h-[44px] text-sm text-slate-500 hover:text-slate-800" data-testid="driver-w9-done-back">
                Back to rides
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="bg-white border border-slate-200 rounded-md p-5">
              <div className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500 mb-3">Your information</div>

              <div className="space-y-3">
                <div>
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Name</label>
                    {!editingName && (
                      <button onClick={() => setEditingName(true)} className="text-[11px] text-[#005E82] hover:underline" data-testid="w9-edit-name">
                        Edit
                      </button>
                    )}
                  </div>
                  <input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    readOnly={!editingName}
                    className={`mt-1 w-full text-sm px-3 py-3 rounded-md border ${editingName ? "border-slate-300 bg-white" : "border-slate-200 bg-slate-50 text-slate-700"}`}
                    data-testid="w9-name"
                  />
                </div>

                <div>
                  <label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Business name (optional)</label>
                  <input
                    value={businessName}
                    onChange={(e) => setBusinessName(e.target.value)}
                    placeholder="Disregarded entity name, if any"
                    className="mt-1 w-full text-sm px-3 py-3 rounded-md border border-slate-200"
                    data-testid="w9-business-name"
                  />
                </div>

                <div>
                  <label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Federal tax classification</label>
                  <div className="mt-1.5 flex flex-col gap-2">
                    {CLASSIFICATIONS.map((c) => (
                      <label key={c.value} className="flex items-center gap-2 text-sm min-h-[44px]">
                        <input
                          type="radio"
                          name="w9classification"
                          value={c.value}
                          checked={classification === c.value}
                          onChange={() => setClassification(c.value)}
                          data-testid={`w9-classification-${c.value}`}
                        />
                        {c.label}
                      </label>
                    ))}
                  </div>

                  {classification === "llc" && (
                    <div className="mt-3">
                      <label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Tax classification (C, S or P)</label>
                      <select
                        value={llcClass}
                        onChange={(e) => setLlcClass(e.target.value)}
                        className="mt-1 w-full text-sm px-3 py-3 rounded-md border border-slate-200 bg-white"
                        data-testid="w9-llc-class"
                      >
                        <option value="">Choose one</option>
                        <option value="C">C = C corporation</option>
                        <option value="S">S = S corporation</option>
                        <option value="P">P = Partnership</option>
                      </select>
                      {!llcClass && (
                        <div className="mt-1.5 text-xs text-amber-700" data-testid="w9-llc-class-hint">
                          Required for an LLC. Ask your accountant if you are not sure which one applies.
                        </div>
                      )}
                    </div>
                  )}

                  {classification === "other" && (
                    <div className="mt-3">
                      <label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Describe your classification</label>
                      <input
                        value={otherDescription}
                        onChange={(e) => setOtherDescription(e.target.value)}
                        placeholder="e.g. Trust, estate"
                        className="mt-1 w-full text-sm px-3 py-3 rounded-md border border-slate-200"
                        data-testid="w9-other-description"
                      />
                      {!otherDescription.trim() && (
                        <div className="mt-1.5 text-xs text-amber-700" data-testid="w9-other-description-hint">
                          Required when Other is selected.
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div>
                  <label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Address</label>
                  <input
                    value={addrStreet}
                    onChange={(e) => setAddrStreet(e.target.value)}
                    placeholder="Street address"
                    className="mt-1 w-full text-sm px-3 py-3 rounded-md border border-slate-200"
                    data-testid="w9-addr-street"
                  />
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <input
                    value={addrCity}
                    onChange={(e) => setAddrCity(e.target.value)}
                    placeholder="City"
                    className="text-sm px-3 py-3 rounded-md border border-slate-200"
                    data-testid="w9-addr-city"
                  />
                  <input
                    value={addrState}
                    onChange={(e) => setAddrState(e.target.value.toUpperCase().slice(0, 2))}
                    placeholder="State"
                    className="text-sm px-3 py-3 rounded-md border border-slate-200"
                    data-testid="w9-addr-state"
                  />
                  <input
                    value={addrZip}
                    onChange={(e) => setAddrZip(digitsOnly(e.target.value, 5))}
                    placeholder="ZIP"
                    inputMode="numeric"
                    className="text-sm px-3 py-3 rounded-md border border-slate-200"
                    data-testid="w9-addr-zip"
                  />
                </div>
              </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-md p-5">
              <div className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500 mb-3">Taxpayer ID</div>
              <div className="flex gap-4 text-sm mb-3">
                <label className="flex items-center gap-2 min-h-[44px]">
                  <input type="radio" name="w9idtype" checked={idType === "ssn"} onChange={() => setIdType("ssn")} data-testid="w9-idtype-ssn" />
                  SSN
                </label>
                <label className="flex items-center gap-2 min-h-[44px]">
                  <input type="radio" name="w9idtype" checked={idType === "ein"} onChange={() => setIdType("ein")} data-testid="w9-idtype-ein" />
                  EIN
                </label>
              </div>

              {idType === "ssn" ? (
                <div className="flex items-center gap-2" data-testid="w9-ssn-row">
                  <input
                    value={ssn1}
                    onChange={(e) => setSsn1(digitsOnly(e.target.value, 3))}
                    maxLength={3}
                    inputMode="numeric"
                    autoComplete="off"
                    placeholder="XXX"
                    className="w-16 min-h-[44px] text-center text-sm px-2 py-3 rounded-md border border-slate-200 font-mono"
                    data-testid="w9-ssn1"
                  />
                  <span className="text-slate-400">-</span>
                  <input
                    value={ssn2}
                    onChange={(e) => setSsn2(digitsOnly(e.target.value, 2))}
                    maxLength={2}
                    inputMode="numeric"
                    autoComplete="off"
                    placeholder="XX"
                    className="w-14 min-h-[44px] text-center text-sm px-2 py-3 rounded-md border border-slate-200 font-mono"
                    data-testid="w9-ssn2"
                  />
                  <span className="text-slate-400">-</span>
                  <input
                    value={ssn3}
                    onChange={(e) => setSsn3(digitsOnly(e.target.value, 4))}
                    maxLength={4}
                    inputMode="numeric"
                    autoComplete="off"
                    placeholder="XXXX"
                    className="w-20 min-h-[44px] text-center text-sm px-2 py-3 rounded-md border border-slate-200 font-mono"
                    data-testid="w9-ssn3"
                  />
                </div>
              ) : (
                <div className="flex items-center gap-2" data-testid="w9-ein-row">
                  <input
                    value={ein1}
                    onChange={(e) => setEin1(digitsOnly(e.target.value, 2))}
                    maxLength={2}
                    inputMode="numeric"
                    autoComplete="off"
                    placeholder="XX"
                    className="w-14 min-h-[44px] text-center text-sm px-2 py-3 rounded-md border border-slate-200 font-mono"
                    data-testid="w9-ein1"
                  />
                  <span className="text-slate-400">-</span>
                  <input
                    value={ein2}
                    onChange={(e) => setEin2(digitsOnly(e.target.value, 7))}
                    maxLength={7}
                    inputMode="numeric"
                    autoComplete="off"
                    placeholder="XXXXXXX"
                    className="w-28 min-h-[44px] text-center text-sm px-2 py-3 rounded-md border border-slate-200 font-mono"
                    data-testid="w9-ein2"
                  />
                </div>
              )}
            </div>

            <div className="bg-white border border-slate-200 rounded-md p-5">
              <div className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500 mb-3">Signature</div>
              <label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Type your full legal name to sign</label>
              <input
                value={signature}
                onChange={(e) => setSignature(e.target.value)}
                placeholder={name}
                className="mt-1 w-full text-sm px-3 py-3 rounded-md border border-slate-200"
                data-testid="w9-signature"
              />
              {signature.trim().length > 0 && !signatureMatches && (
                <div className="mt-1.5 text-xs text-amber-700" data-testid="w9-signature-hint">
                  Type your name exactly as it appears on your license.
                </div>
              )}
              <div className="mt-3 text-xs text-slate-500 leading-relaxed">
                By selecting Sign and file, I agree this electronic signature is the legal equivalent of my handwritten signature.
              </div>
            </div>

            {previewUrl && (
              <div className="bg-white border border-slate-200 rounded-md p-3" data-testid="w9-preview">
                <iframe title="W-9 preview" src={previewUrl} className="w-full rounded" style={{ height: 420 }} />
              </div>
            )}

            <div className="grid grid-cols-1 gap-2 pb-2">
              <button
                onClick={doPreview}
                disabled={busy || !name.trim()}
                className="min-h-[44px] flex items-center justify-center gap-2 border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 text-sm rounded-md py-3 disabled:opacity-60"
                data-testid="w9-preview-btn"
              >
                <FileDown size={14} /> Preview PDF
              </button>
              <button
                onClick={doSign}
                disabled={!canSign}
                className="min-h-[44px] flex items-center justify-center gap-2 bg-[#0A192F] hover:bg-[#112240] text-white text-sm rounded-md py-3 disabled:opacity-60"
                data-testid="w9-sign-btn"
              >
                <CheckCircle2 size={14} /> Sign and file
              </button>
            </div>

            <div className="text-center">
              <button onClick={() => navigate("/driver")} className="min-h-[44px] text-xs text-slate-500 hover:text-slate-800" data-testid="w9-cancel-back">
                Already have a signed copy? Go back and upload it from Docs instead.
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
